import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, List } from 'lucide-react';
import Compass3D from './Compass3D';
import ScaleBar from './ScaleBar';
import { MorphPanel } from './shared/MorphPanel';
import { projectService } from '../services/projectService';
import { demoProjectsService } from '../services/demoProjectsService';
import { useAuth } from '../contexts/AuthContext';
import { useUserProjectsContext } from '../contexts/UserProjectsContext';
import { useUnitPreferences } from '../hooks/useUnitPreferences';
import { metersToFeet, slopeDegreesToPercentGrade } from '../utils/units';
import compassRing from '../../assets/compass_ring.png';
import { cleanBreakdown, filterBreakdownToVisible, getVisibleArtifactNames, getContourElevationInfo, hasVisibleCombined, hasVisibleTwi, hasVisibleFlow, getVisibleTwiPercentiles, formatContourIntervalLabel, } from '../utils/legendData';
import { TWI_LOW_RGB, TWI_HIGH_RGB, rgbHex } from './MeasureTool/elevationColors';
const tryParseJson = (raw) => {
    if (!raw || typeof raw !== 'string')
        return null;
    try {
        return JSON.parse(raw);
    }
    catch (_a) {
        return null;
    }
};
const detectArtifactCategory = (artifact) => {
    var _a;
    try {
        const name = typeof ((_a = artifact.metadata) === null || _a === void 0 ? void 0 : _a.getName) === 'function' ? artifact.metadata.getName() : '';
        const lowerName = name.toLowerCase();
        if (lowerName.includes('aspect'))
            return 'aspect';
        if (lowerName.includes('slope'))
            return 'slope';
        if (lowerName.includes('elevation') ||
            lowerName.includes('contour') ||
            lowerName.includes('contours') ||
            lowerName.includes('colored_hillshade') ||
            lowerName.includes('colored hillshade') ||
            // Specific contour friendly names
            lowerName === '0.5m' ||
            lowerName === '1m' ||
            lowerName === '2m' ||
            lowerName === '3m') {
            return 'elevation';
        }
        if (lowerName.includes('geomorphon'))
            return 'geomorphons';
        return 'other';
    }
    catch (error) {
        console.error('Error in detectArtifactCategory:', error);
        return 'other';
    }
};
export const LegendOverlay = ({ viewer, loadedArtifacts, isArtifactVisible, selectedProject, isPolygonDrawing = false, artifactControls }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const { isAuthenticated } = useAuth();
    const { projects: userProjects } = useUserProjectsContext();
    const [statsByProjectId, setStatsByProjectId] = useState({});
    // Seeded from the app-wide unit preference on mount, then a local override:
    // the m/ft buttons below stay a one-off toggle for this legend.
    const { system: appUnitSystem } = useUnitPreferences();
    const [elevationUnit, setElevationUnit] = useState(() => appUnitSystem === 'imperial' ? 'ft' : 'm');
    const selectedProjectId = useMemo(() => {
        var _a;
        try {
            return ((_a = selectedProject === null || selectedProject === void 0 ? void 0 : selectedProject.getId) === null || _a === void 0 ? void 0 : _a.call(selectedProject)) || '';
        }
        catch (_b) {
            return '';
        }
    }, [selectedProject]);
    // Check if selected project is a demo project
    const isDemoProject = useMemo(() => {
        if (!selectedProjectId)
            return false;
        // If the project ID is not found in user projects, it's a demo project
        return !userProjects.some(userProject => userProject.id === selectedProjectId);
    }, [selectedProjectId, userProjects]);
    // Determine if aspect/slope/elevation/geomorphons artifacts are visible
    const visibility = useMemo(() => {
        let hasAspect = false;
        let hasSlope = false;
        let hasElevation = false;
        let hasGeomorphons = false;
        try {
            loadedArtifacts.forEach((artifact, artifactId) => {
                if (isArtifactVisible(artifactId)) {
                    const cat = detectArtifactCategory(artifact);
                    if (cat === 'aspect')
                        hasAspect = true;
                    if (cat === 'slope')
                        hasSlope = true;
                    if (cat === 'elevation')
                        hasElevation = true;
                    if (cat === 'geomorphons')
                        hasGeomorphons = true;
                }
            });
        }
        catch (_a) { }
        return { hasAspect, hasSlope, hasElevation, hasGeomorphons };
    }, [loadedArtifacts, isArtifactVisible]);
    // Per-class filtering: aspect/slope are split into per-class layers, so the
    // legend should show only the classes whose layer is visible. A combined
    // (all-classes) raster instead shows the full breakdown.
    const visibleNames = useMemo(() => getVisibleArtifactNames(loadedArtifacts, isArtifactVisible), [loadedArtifacts, isArtifactVisible]);
    const combinedAspect = useMemo(() => hasVisibleCombined(loadedArtifacts, isArtifactVisible, 'aspect'), [loadedArtifacts, isArtifactVisible]);
    const combinedSlope = useMemo(() => hasVisibleCombined(loadedArtifacts, isArtifactVisible, 'slope'), [loadedArtifacts, isArtifactVisible]);
    // TWI / flow accumulation — continuous Blues gradients (qualitative for now).
    const showTwi = useMemo(() => hasVisibleTwi(loadedArtifacts, isArtifactVisible), [loadedArtifacts, isArtifactVisible]);
    const showFlow = useMemo(() => hasVisibleFlow(loadedArtifacts, isArtifactVisible), [loadedArtifacts, isArtifactVisible]);
    const twiPercentiles = useMemo(() => getVisibleTwiPercentiles(loadedArtifacts, isArtifactVisible), [loadedArtifacts, isArtifactVisible]);
    // Contours/elevation: robust detection (any interval), interval label, and
    // gradient suppressed when contours are monochrome.
    const contourInfo = useMemo(() => getContourElevationInfo(loadedArtifacts, isArtifactVisible, artifactControls), [loadedArtifacts, isArtifactVisible, artifactControls]);
    const statistics = useMemo(() => {
        var _a;
        if (!selectedProjectId)
            return null;
        return (_a = statsByProjectId[selectedProjectId]) !== null && _a !== void 0 ? _a : null;
    }, [statsByProjectId, selectedProjectId]);
    // Track artifact count to detect when new layers become available
    const artifactCountsRef = useRef({});
    // Clear cached statistics when new artifacts become available for a project
    useEffect(() => {
        if (!selectedProjectId)
            return;
        const currentArtifactCount = Array.from(loadedArtifacts.keys()).filter(artifactId => artifactId.includes(selectedProjectId)).length;
        const previousCount = artifactCountsRef.current[selectedProjectId] || 0;
        // If we have more artifacts than before, invalidate the cached statistics
        if (currentArtifactCount > previousCount) {
            setStatsByProjectId((prev) => {
                // Only clear if we actually have cached stats for this project
                if (selectedProjectId in prev) {
                    const updated = { ...prev };
                    delete updated[selectedProjectId];
                    return updated;
                }
                return prev;
            });
        }
        // Update the artifact count tracking
        artifactCountsRef.current[selectedProjectId] = currentArtifactCount;
    }, [selectedProjectId, loadedArtifacts]);
    // Fetch project statistics only when expanded and needed
    useEffect(() => {
        let cancelled = false;
        // Check if we have any loaded artifacts (indicates project has been processed)
        // Since loadedArtifacts should only contain artifacts from the currently selected project,
        // we just need to check if any artifacts are loaded
        const hasAnyArtifacts = loadedArtifacts.size > 0;
        const shouldFetch = isExpanded && selectedProjectId && hasAnyArtifacts && !(selectedProjectId in statsByProjectId);
        if (!shouldFetch)
            return;
        (async () => {
            try {
                let fetched;
                if (isDemoProject) {
                    // Use demo projects service for demo projects
                    fetched = await demoProjectsService.getProjectStatistics(selectedProjectId);
                }
                else {
                    // Use regular project service for user projects
                    fetched = await projectService.getProjectStatistics(selectedProjectId);
                }
                if (cancelled)
                    return;
                setStatsByProjectId((prev) => ({ ...prev, [selectedProjectId]: fetched ? {
                        aspectBreakdown: fetched.aspectBreakdown,
                        slopeBreakdown: fetched.slopeBreakdown,
                        elevationBreakdown: fetched.elevationBreakdown,
                        geomorphonsBreakdown: fetched.geomorphonsBreakdown
                    } : null }));
            }
            catch (error) {
                console.error('Error fetching statistics:', error);
                if (cancelled)
                    return;
                setStatsByProjectId((prev) => ({ ...prev, [selectedProjectId]: null }));
            }
        })();
        return () => { cancelled = true; };
    }, [isExpanded, selectedProjectId, loadedArtifacts, statsByProjectId, isDemoProject]);
    // Retry mechanism: if a layer is visible but legend data is missing, retry fetching statistics
    useEffect(() => {
        if (!isExpanded || !selectedProjectId || !statistics)
            return;
        let needsRetry = false;
        // Check if any visible layer types are missing their legend data
        if (visibility.hasAspect && (!statistics.aspectBreakdown || statistics.aspectBreakdown.length === 0)) {
            needsRetry = true;
        }
        if (visibility.hasSlope && (!statistics.slopeBreakdown || statistics.slopeBreakdown.length === 0)) {
            needsRetry = true;
        }
        if (visibility.hasElevation && (!statistics.elevationBreakdown || statistics.elevationBreakdown.length === 0)) {
            needsRetry = true;
        }
        if (visibility.hasGeomorphons && (!statistics.geomorphonsBreakdown || statistics.geomorphonsBreakdown.length === 0)) {
            needsRetry = true;
        }
        if (!needsRetry)
            return;
        // Retry after a short delay
        const retryTimer = setTimeout(async () => {
            try {
                let fetched;
                if (isDemoProject) {
                    // Use demo projects service for demo projects
                    fetched = await demoProjectsService.getProjectStatistics(selectedProjectId);
                }
                else {
                    // Use regular project service for user projects
                    fetched = await projectService.getProjectStatistics(selectedProjectId);
                }
                if (fetched) {
                    setStatsByProjectId((prev) => ({ ...prev, [selectedProjectId]: {
                            aspectBreakdown: fetched.aspectBreakdown,
                            slopeBreakdown: fetched.slopeBreakdown,
                            elevationBreakdown: fetched.elevationBreakdown,
                            geomorphonsBreakdown: fetched.geomorphonsBreakdown
                        } }));
                }
            }
            catch (error) {
                console.warn('Statistics retry failed:', error);
            }
        }, 2000); // 2 second delay
        return () => clearTimeout(retryTimer);
    }, [isExpanded, selectedProjectId, statistics, visibility, isDemoProject]);
    const toggleExpanded = useCallback(() => setIsExpanded((v) => !v), []);
    // Utility function for elevation formatting
    const formatElevation = useCallback((meters, unit) => {
        const value = unit === 'ft' ? metersToFeet(meters) : meters;
        return `${Math.round(value)}${unit}`;
    }, []);
    // Prepare legend items
    const aspectItems = useMemo(() => combinedAspect
        ? cleanBreakdown(statistics === null || statistics === void 0 ? void 0 : statistics.aspectBreakdown)
        : filterBreakdownToVisible(statistics === null || statistics === void 0 ? void 0 : statistics.aspectBreakdown, visibleNames), [statistics === null || statistics === void 0 ? void 0 : statistics.aspectBreakdown, combinedAspect, visibleNames]);
    // All aspect directions visible (or a combined raster) → show the compass
    // ring; a subset → show swatches of just the visible directions.
    const allAspectVisible = useMemo(() => {
        if (combinedAspect)
            return true;
        const full = cleanBreakdown(statistics === null || statistics === void 0 ? void 0 : statistics.aspectBreakdown).length;
        return aspectItems.length > 0 && aspectItems.length === full;
    }, [combinedAspect, statistics === null || statistics === void 0 ? void 0 : statistics.aspectBreakdown, aspectItems.length]);
    const elevationItems = useMemo(() => {
        const list = (statistics === null || statistics === void 0 ? void 0 : statistics.elevationBreakdown) || [];
        return Array.isArray(list) ? list.filter((i) => i && i.title && i.color) : [];
    }, [statistics === null || statistics === void 0 ? void 0 : statistics.elevationBreakdown]);
    // Extract min/max elevation from elevation breakdown for continuous legend
    const elevationRange = useMemo(() => {
        if (!elevationItems || !elevationItems.length)
            return null;
        // Parse elevation values from keys (format: "min_max")
        const elevations = [];
        elevationItems.forEach(item => {
            if ((item === null || item === void 0 ? void 0 : item.key) && typeof item.key === 'string') {
                const parts = item.key.split('_');
                if (parts.length >= 2) {
                    const min = parseFloat(parts[0]);
                    const max = parseFloat(parts[1]);
                    if (!isNaN(min) && isFinite(min))
                        elevations.push(min);
                    if (!isNaN(max) && isFinite(max))
                        elevations.push(max);
                }
            }
        });
        if (elevations.length === 0)
            return null;
        const min = Math.min(...elevations);
        const max = Math.max(...elevations);
        if (!isFinite(min) || !isFinite(max) || min === max)
            return null;
        const mid = (min + max) / 2;
        return { min, max, mid };
    }, [elevationItems]);
    const slopeItems = useMemo(() => combinedSlope
        ? cleanBreakdown(statistics === null || statistics === void 0 ? void 0 : statistics.slopeBreakdown)
        : filterBreakdownToVisible(statistics === null || statistics === void 0 ? void 0 : statistics.slopeBreakdown, visibleNames), [statistics === null || statistics === void 0 ? void 0 : statistics.slopeBreakdown, combinedSlope, visibleNames]);
    const geomorphonsItems = useMemo(() => {
        const list = (statistics === null || statistics === void 0 ? void 0 : statistics.geomorphonsBreakdown) || [];
        return Array.isArray(list) ? list.filter((i) => i && i.title && i.color) : [];
    }, [statistics === null || statistics === void 0 ? void 0 : statistics.geomorphonsBreakdown]);
    // Drive aspect/slope off the (visible-class-filtered) items, not the
    // name-based category flag — per-class layers ("East (E)") don't contain the
    // word "aspect"/"slope", so the category flag misses them.
    const showAspectLegend = aspectItems.length > 0;
    const showSlopeLegend = slopeItems.length > 0;
    // Show the elevation gradient only when a non-monochrome colored elevation/
    // contour layer is visible (monochrome contours hide their ramp).
    const showElevationLegend = contourInfo.showGradient && elevationItems.length > 0;
    const showGeomorphonsLegend = visibility.hasGeomorphons && geomorphonsItems.length > 0;
    // Aspect ring helpers
    const directionAngles = useMemo(() => ({
        n: 0, nne: 22.5, ne: 45, ene: 67.5,
        e: 90, ese: 112.5, se: 135, sse: 157.5,
        s: 180, ssw: 202.5, sw: 225, wsw: 247.5,
        w: 270, wnw: 292.5, nw: 315, nnw: 337.5
    }), []);
    const orderedAspectColors = useMemo(() => {
        if (!showAspectLegend)
            return [];
        const parsed = aspectItems.map((it) => {
            const keyRaw = (it.key || it.title || '').toString().toLowerCase().replace(/\s+/g, '');
            // Normalize some common forms
            const norm = keyRaw
                .replace(/north/g, 'n')
                .replace(/south/g, 's')
                .replace(/east/g, 'e')
                .replace(/west/g, 'w');
            // Try to find a matching direction token in order of longest first
            const tokens = ['nne', 'ene', 'ese', 'sse', 'ssw', 'wsw', 'wnw', 'nnw', 'ne', 'se', 'sw', 'nw', 'n', 'e', 's', 'w'];
            let angle = null;
            for (const t of tokens) {
                if (norm.includes(t)) {
                    angle = directionAngles[t];
                    break;
                }
            }
            return { color: it.color, angle };
        });
        const known = parsed.filter(p => p.angle !== null).sort((a, b) => a.angle - b.angle);
        const unknown = parsed.filter(p => p.angle === null);
        const colors = known.map(k => k.color);
        // Append unknown colors (preserve input order) at the end to keep continuity
        unknown.forEach(u => colors.push(u.color));
        if (colors.length === 0)
            return [];
        return colors;
    }, [showAspectLegend, aspectItems, directionAngles]);
    const aspectConicGradient = useMemo(() => {
        if (!orderedAspectColors.length)
            return '';
        const n = orderedAspectColors.length;
        const step = 360 / n;
        // Use boundary-only stops to let colors fade between segments
        const stops = [];
        for (let i = 0; i < n; i += 1) {
            const angle = i * step;
            const color = orderedAspectColors[i];
            stops.push(`${color} ${angle}deg`);
        }
        // Close the loop by repeating the first color at 360deg
        stops.push(`${orderedAspectColors[0]} 360deg`);
        return `conic-gradient(${stops.join(', ')})`;
    }, [orderedAspectColors]);
    const renderAspectRing = useCallback(() => {
        if (!aspectConicGradient)
            return null;
        const size = 130; // compact to fit panel
        const outerInset = 12; // push ring further inside PNG
        const ringThickness = 16; // make the band thinner so it looks like a ring
        const innerInset = outerInset + ringThickness; // thickness = innerInset - outerInset
        const ringYOffset = 1; // nudge ring up to align with PNG
        const radius = size / 2;
        const tickOuter = radius - 1; // align edge precisely
        const majorTickLen = 12;
        const tickStyle = (angleDeg, len) => ({
            position: 'absolute',
            top: '50%', left: '50%',
            width: 2, height: len,
            backgroundColor: 'var(--sand)',
            transform: `translate(-50%, -50%) rotate(${angleDeg}deg) translateY(-${tickOuter}px)`,
            transformOrigin: '50% 0%'
        });
        const labelStyle = (angleDeg) => ({
            position: 'absolute',
            top: '50%', left: '50%',
            transform: `translate(-50%, -50%) rotate(${angleDeg}deg) translateY(-${tickOuter + 16}px) rotate(${-angleDeg}deg)`,
            color: 'var(--sand)',
            fontSize: 12,
            fontWeight: 700
        });
        return (_jsx("div", { className: "mt-2 w-full flex items-center justify-center", children: _jsxs("div", { className: "relative", style: { width: size, height: size }, children: [_jsx("div", { className: "absolute rounded-full", style: {
                            top: outerInset - ringYOffset,
                            bottom: outerInset + ringYOffset,
                            left: outerInset,
                            right: outerInset,
                            background: aspectConicGradient
                        } }), _jsx("div", { className: "absolute rounded-full", style: {
                            top: innerInset - ringYOffset,
                            bottom: innerInset + ringYOffset,
                            left: innerInset,
                            right: innerInset,
                            backgroundColor: 'var(--charcoal)'
                        } }), _jsx("img", { src: compassRing, alt: "", className: "absolute inset-0", style: { width: size, height: size, pointerEvents: 'none' } })] }) }));
    }, [aspectConicGradient]);
    // When only a subset of aspect directions is visible, a full compass ring is
    // misleading — list the visible directions as swatches instead.
    const renderAspectSwatches = useCallback(() => (_jsx("div", { className: "mt-2 space-y-1", children: aspectItems.map((item) => (_jsxs("div", { className: "flex items-center gap-2 text-xs", children: [_jsx("div", { className: "w-4 h-4 rounded", style: { backgroundColor: item.color, border: '1px solid var(--pewter)' } }), _jsx("span", { style: { color: 'var(--sand)' }, children: item.title })] }, item.key || item.title))) })), [aspectItems]);
    // Slope spark helpers
    const parseMaxDegreesFromSlopeTitle = (title) => {
        if (!title)
            return null;
        const t = title.toLowerCase();
        const range = t.match(/(\d+(?:\.\d+)?)\s*[-–]\s*(\d+(?:\.\d+)?)\s*°?/);
        if (range) {
            const hi = parseFloat(range[2]);
            if (!Number.isNaN(hi))
                return hi;
        }
        const le = t.match(/(?:<=|<|to)\s*(\d+(?:\.\d+)?)\s*°?/);
        if (le) {
            const hi = parseFloat(le[1]);
            if (!Number.isNaN(hi))
                return hi;
        }
        const ge = t.match(/(?:>=|>)\s*(\d+(?:\.\d+)?)\s*°?/);
        if (ge) {
            const low = parseFloat(ge[1]);
            if (!Number.isNaN(low))
                return low;
        }
        const plus = t.match(/(\d+(?:\.\d+)?)\s*\+\s*°?/);
        if (plus) {
            const val = parseFloat(plus[1]);
            if (!Number.isNaN(val))
                return val;
        }
        const single = t.match(/(\d+(?:\.\d+)?)\s*°/);
        if (single) {
            const val = parseFloat(single[1]);
            if (!Number.isNaN(val))
                return val;
        }
        return null;
    };
    const inferredMaxSlopeDeg = useMemo(() => {
        if (!showSlopeLegend)
            return null;
        let maxDeg = 0;
        slopeItems.forEach((it) => {
            // Prefer bins that are present; if percent is defined and zero, skip
            const pct = typeof it.percent === 'number' ? it.percent : undefined;
            if (pct !== undefined && pct <= 0)
                return;
            const parsed = parseMaxDegreesFromSlopeTitle(it.title);
            if (parsed !== null && parsed > maxDeg)
                maxDeg = parsed;
        });
        return maxDeg > 0 ? maxDeg : null;
    }, [showSlopeLegend, slopeItems]);
    // Classification ranges by name (fallback when numbers are not in the title)
    const slopeClassRanges = useMemo(() => ({
        'flat': { min: 0, max: 2 },
        'gentle slope': { min: 2, max: 4 },
        'moderate slope': { min: 4, max: 8 },
        'strong slope': { min: 8, max: 12 },
        'moderately steep': { min: 12, max: 18 },
        'steep': { min: 18, max: 24 },
        'very steep': { min: 24, max: 30 },
        'extremely steep': { min: 30, max: 36 },
        'excessively steep': { min: 36, max: 45 },
        'hazardously steep': { min: 45, max: 90 }
    }), []);
    const getSlopeRange = useCallback((title) => {
        const parsedMax = parseMaxDegreesFromSlopeTitle(title || '');
        // Try to parse min via range pattern
        const t = (title || '').toLowerCase();
        const range = t.match(/(\d+(?:\.\d+)?)\s*[-–]\s*(\d+(?:\.\d+)?)\s*°?/);
        if (range) {
            const lo = parseFloat(range[1]);
            const hi = parseFloat(range[2]);
            if (!Number.isNaN(lo) && !Number.isNaN(hi))
                return { min: lo, max: hi };
        }
        // Fallback to named class mapping if present
        const key = t.trim();
        if (key in slopeClassRanges)
            return slopeClassRanges[key];
        // If only a single bound was found (e.g., "> 30°"), infer min=that, max=that
        if (parsedMax !== null)
            return { min: parsedMax, max: parsedMax };
        return null;
    }, [slopeClassRanges]);
    const renderSlopeSparkList = useCallback(() => {
        if (!showSlopeLegend)
            return null;
        const tile = 22; // square tile size
        const diag = Math.ceil(tile * 1.5); // long enough to cross
        return (_jsx("div", { className: "mt-2 space-y-2", children: slopeItems.map((it, idx) => {
                var _a;
                const rng = getSlopeRange(it.title);
                const maxValue = rng ? rng.max : ((_a = parseMaxDegreesFromSlopeTitle(it.title)) !== null && _a !== void 0 ? _a : 0);
                const angle = Math.max(0, Math.min(90, maxValue));
                // Surface percent grade alongside the degree range (tan(θ)×100). The
                // open-ended top class (max ≥ 90°) is shown as ">N%".
                let gradeSuffix = '';
                if (rng) {
                    const minPct = Math.round(slopeDegreesToPercentGrade(rng.min));
                    gradeSuffix = rng.max >= 90
                        ? `> ${minPct}%`
                        : `${minPct}–${Math.round(slopeDegreesToPercentGrade(rng.max))}%`;
                }
                return (_jsxs("div", { className: "flex items-center gap-2", children: [_jsxs("div", { className: "min-w-0 flex-1", children: [_jsx("div", { className: "text-xs", style: { color: 'var(--sand)' }, children: it.title }), gradeSuffix && (_jsx("div", { className: "text-[10px]", style: { color: 'var(--dew)' }, children: gradeSuffix }))] }), _jsx("div", { className: "relative rounded", style: { width: tile, height: tile, backgroundColor: it.color, border: '1px solid var(--pewter)' }, children: _jsx("div", { className: "absolute left-1/2 top-1/2", style: {
                                    width: diag,
                                    height: 2,
                                    backgroundColor: 'white',
                                    boxShadow: '0 0 1px rgba(0,0,0,0.6)',
                                    transform: `translate(-50%, -50%) rotate(${angle}deg)`,
                                    transformOrigin: '50% 50%'
                                } }) })] }, `slope-spark-${idx}`));
            }) }));
    }, [showSlopeLegend, slopeItems, getSlopeRange]);
    // Generate continuous elevation gradient from elevation items colors
    const elevationGradient = useMemo(() => {
        if (!elevationItems.length)
            return '';
        // Sort elevation items by elevation value (parsed from key)
        const sortedItems = [...elevationItems].sort((a, b) => {
            var _a, _b;
            const aMin = parseFloat(((_a = a.key) === null || _a === void 0 ? void 0 : _a.split('_')[0]) || '0');
            const bMin = parseFloat(((_b = b.key) === null || _b === void 0 ? void 0 : _b.split('_')[0]) || '0');
            return aMin - bMin;
        });
        if (sortedItems.length === 0)
            return '';
        // Create gradient stops
        const stops = sortedItems.map((item, index) => {
            const percentage = sortedItems.length === 1 ? 50 : (index / (sortedItems.length - 1)) * 100;
            return `${item.color} ${percentage}%`;
        });
        return `linear-gradient(to top, ${stops.join(', ')})`;
    }, [elevationItems]);
    const renderElevationThermometer = useCallback(() => {
        if (!elevationRange || !elevationGradient || typeof elevationRange.min !== 'number' || typeof elevationRange.max !== 'number') {
            return null;
        }
        const thermometerHeight = 120;
        const thermometerWidth = 44; // 2x wider (was 22, now 44)
        try {
            return (_jsxs("div", { className: "mt-2 flex flex-col items-center gap-3", children: [_jsxs("div", { className: "flex items-stretch gap-3", children: [_jsx("div", { className: "flex flex-col items-center", children: _jsx("div", { className: "rounded-md border", style: {
                                        width: thermometerWidth,
                                        height: thermometerHeight,
                                        background: elevationGradient,
                                        border: '1px solid var(--pewter)'
                                    } }) }), _jsxs("div", { className: "flex flex-col justify-between text-xs", style: { height: thermometerHeight, color: 'var(--sand)' }, children: [_jsx("div", { children: formatElevation(elevationRange.max, elevationUnit) }), _jsx("div", { children: formatElevation(elevationRange.mid, elevationUnit) }), _jsx("div", { children: formatElevation(elevationRange.min, elevationUnit) })] })] }), _jsxs("div", { className: "flex rounded overflow-hidden border", style: { borderColor: 'var(--pewter)' }, children: [_jsx("button", { onClick: () => setElevationUnit('m'), className: `px-2 py-1 text-xs ${elevationUnit === 'm' ? 'font-semibold' : ''}`, style: {
                                    backgroundColor: elevationUnit === 'm' ? 'var(--sand)' : 'var(--charcoal)',
                                    color: elevationUnit === 'm' ? 'var(--forest)' : 'var(--dew)'
                                }, title: "Show elevation in meters", children: "m" }), _jsx("button", { onClick: () => setElevationUnit('ft'), className: `px-2 py-1 text-xs ${elevationUnit === 'ft' ? 'font-semibold' : ''}`, style: {
                                    backgroundColor: elevationUnit === 'ft' ? 'var(--sand)' : 'var(--charcoal)',
                                    color: elevationUnit === 'ft' ? 'var(--forest)' : 'var(--dew)'
                                }, title: "Show elevation in feet", children: "ft" })] })] }));
        }
        catch (error) {
            console.error('Error rendering elevation thermometer:', error);
            return null;
        }
    }, [elevationRange, elevationGradient, elevationUnit, formatElevation]);
    return (_jsxs("div", { className: `fixed right-3 pointer-events-auto select-none flex flex-col items-end gap-2 ${isPolygonDrawing
            ? 'bottom-36 sm:bottom-6 z-40 sm:z-30'
            : 'bottom-6 z-30'}`, children: [!isPolygonDrawing && (_jsx(_Fragment, { children: _jsxs(MorphPanel, { collapsed: !isExpanded, onToggle: toggleExpanded, icon: _jsx(List, { size: 20 }), label: "Legend", anchor: "bottom-right", expandedWidth: 340, maxHeight: "calc(100vh - 180px)", children: [_jsxs("div", { className: "p-3 border-b flex items-center justify-between flex-shrink-0 cursor-pointer", style: { borderColor: 'var(--pewter)' }, onClick: toggleExpanded, children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("span", { className: "font-heading text-sm font-semibold", style: { color: 'var(--sand)' }, children: "Legend" }), _jsx(List, { size: 16, style: { color: 'var(--sand)' } })] }), _jsx(ChevronDown, { size: 16 })] }), _jsx("div", { className: "px-3 pt-3 pb-3 space-y-3 overflow-y-auto flex-1 min-h-0", children: (visibility.hasAspect || visibility.hasSlope || visibility.hasGeomorphons ||
                                showAspectLegend || showSlopeLegend || showElevationLegend || showGeomorphonsLegend ||
                                showTwi || showFlow || twiPercentiles.length > 0 || contourInfo.hasAny) ? (_jsxs(_Fragment, { children: [showAspectLegend && (_jsxs("div", { className: "rounded-md border p-2", style: { borderColor: 'var(--pewter)', backgroundColor: 'rgba(255,255,255,0.04)' }, children: [_jsx("div", { className: "text-sm font-medium", style: { color: 'var(--sand)' }, children: "Aspect" }), allAspectVisible ? renderAspectRing() : renderAspectSwatches()] })), showSlopeLegend && (_jsxs("div", { className: "rounded-md border p-2", style: { borderColor: 'var(--pewter)', backgroundColor: 'rgba(255,255,255,0.04)' }, children: [_jsx("div", { className: "text-sm font-medium", style: { color: 'var(--sand)' }, children: "Slope" }), renderSlopeSparkList()] })), (contourInfo.hasAny || showElevationLegend) && (_jsxs("div", { className: "rounded-md border p-2", style: { borderColor: 'var(--pewter)', backgroundColor: 'rgba(255,255,255,0.04)' }, children: [_jsx("div", { className: "text-sm font-medium", style: { color: 'var(--sand)' }, children: "Elevation" }), showElevationLegend && renderElevationThermometer(), contourInfo.monochromeColors.map((c, i) => (_jsxs("div", { className: "mt-2 flex items-center gap-2 text-xs", children: [_jsx("span", { style: { width: 18, height: 3, backgroundColor: c, flexShrink: 0 } }), _jsx("span", { style: { color: 'var(--sand)' }, children: "Contours (monochrome)" })] }, i))), contourInfo.contourIntervals.length > 0 && (_jsxs("div", { className: "mt-2 text-xs", style: { color: 'var(--dew)' }, children: ["Interval:", ' ', contourInfo.contourIntervals
                                                        .map((e) => formatContourIntervalLabel(e, elevationUnit === 'ft' ? 'imperial' : 'metric'))
                                                        .join(' · ')] }))] })), (visibility.hasGeomorphons || showGeomorphonsLegend) && (_jsxs("div", { className: "rounded-md border p-2", style: { borderColor: 'var(--pewter)', backgroundColor: 'rgba(255,255,255,0.04)' }, children: [_jsx("div", { className: "text-sm font-medium", style: { color: 'var(--sand)' }, children: "Geomorphons" }), showGeomorphonsLegend ? (_jsx("div", { className: "mt-2 space-y-1", children: geomorphonsItems.map((item) => (_jsxs("div", { className: "flex items-center gap-2 text-xs", children: [_jsx("div", { className: "w-4 h-4 rounded", style: { backgroundColor: item.color, border: '1px solid var(--pewter)' } }), _jsx("span", { style: { color: 'var(--sand)' }, children: item.title })] }, item.key))) })) : (_jsx("div", { className: "mt-2 text-xs", style: { color: 'var(--dew)' }, children: "Legend not available" }))] })), (showTwi || twiPercentiles.length > 0) && (_jsxs("div", { className: "rounded-md border p-2", style: { borderColor: 'var(--pewter)', backgroundColor: 'rgba(255,255,255,0.04)' }, children: [_jsx("div", { className: "text-sm font-medium", style: { color: 'var(--sand)' }, children: "Wetness (TWI)" }), showTwi && (_jsxs("div", { className: "mt-2 flex items-center gap-2 text-[10px]", style: { color: 'var(--dew)' }, children: [_jsx("span", { children: "Drier" }), _jsx("span", { style: { flex: 1, height: 10, borderRadius: 2, background: `linear-gradient(to right, ${rgbHex(TWI_LOW_RGB)}, ${rgbHex(TWI_HIGH_RGB)})` } }), _jsx("span", { children: "Wetter" })] })), twiPercentiles.length > 0 && (_jsx("div", { className: "mt-2 space-y-1", children: twiPercentiles.map((p) => (_jsxs("div", { className: "flex items-center gap-2 text-xs", children: [_jsx("div", { className: "w-4 h-4 rounded", style: { backgroundColor: rgbHex(TWI_HIGH_RGB), border: '1px solid var(--pewter)' } }), _jsxs("span", { style: { color: 'var(--sand)' }, children: [p.label, " ", _jsxs("span", { style: { color: 'var(--dew)' }, children: ["\u00B7 ", p.pct, "th pct"] })] })] }, p.pct))) }))] })), showFlow && (_jsxs("div", { className: "rounded-md border p-2", style: { borderColor: 'var(--pewter)', backgroundColor: 'rgba(255,255,255,0.04)' }, children: [_jsx("div", { className: "text-sm font-medium", style: { color: 'var(--sand)' }, children: "Flow Accumulation" }), _jsxs("div", { className: "mt-2 flex items-center gap-2 text-[10px]", style: { color: 'var(--dew)' }, children: [_jsx("span", { children: "Less" }), _jsx("span", { style: { flex: 1, height: 10, borderRadius: 2, background: `linear-gradient(to right, ${rgbHex(TWI_LOW_RGB)}, ${rgbHex(TWI_HIGH_RGB)})` } }), _jsx("span", { children: "More" })] })] }))] })) : (_jsx("div", { className: "rounded-md border p-2", style: { borderColor: 'var(--pewter)', backgroundColor: 'rgba(255,255,255,0.04)' }, children: _jsx("div", { className: "text-xs", style: { color: 'var(--dew)' }, children: "Open aspect, slope, elevation, or geomorphons rasters to see the contextual legend." }) })) })] }) })), _jsx(Compass3D, { viewer: viewer, inline: true }), _jsx(ScaleBar, { viewer: viewer, inline: true })] }));
};
export default LegendOverlay;
